import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BadgeCheck, Ban, FileWarning, Printer, Loader2 } from 'lucide-react';
import { T, TIPOGRAFIA, glassLight, primaryButton, Badge } from '../ui';
import { LOGO_DATA_URI } from '../Logo';
import { fetchPedidoPorToken } from './gasData';
import { esWOP, etiquetaTipo, imprimirPedido, suplidorDe } from './pedidoDocumento';
import type { Pedido } from '../types';

// Etiqueta legible de cada producto, duplicada a propósito en forma mínima:
// esta pantalla es pública y no debe arrastrar la lógica del módulo interno.
const PRODUCTO_LABEL: Record<string, string> = {
  jetA1: 'Jet A-1 / Kerosen',
  gasolinaRegular: 'Gasolina Regular',
  gasolinaPremium: 'Gasolina Premium',
  gasoilRegular: 'Gasoil Regular',
  gasoilOptimo: 'Gasoil Óptimo',
  fuelOilReg: 'Fuel Oil Reg.',
  fuelOilPrem: 'Fuel Oil Prem.',
  glp: 'GLP'
};
const EMPRESA_NOMBRE = 'Junquito Gas, S.R.L.';
const fmtGal = (n: number) => new Intl.NumberFormat('es-DO').format(n || 0);
const fmtFecha = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};
function Marco({
  children
}: {
  children: React.ReactNode;
}) {
  return <div style={{
    minHeight: '100vh',
    background: '#FFFCF8',
    fontFamily: TIPOGRAFIA,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px'
  }} data-id="element-1475" data-mp="r051d3u82np6">

      <img src={LOGO_DATA_URI} alt="Junquito Gas" style={{
      height: 72,
      marginBottom: 26
    }} data-id="element-1476" data-mp="10tc38epr7lki" />

      <div style={{
      width: '100%',
      maxWidth: 720
    }} data-id="element-1477" data-mp="1eoo9njd9v0h9">

        {children}
      </div>
    </div>;
}
function Dato({
  label,
  valor
}: {
  label: string;
  valor: string;
}) {
  return <div data-id="element-1478" data-mp="3gkhvcp41hrjs">
      <div style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: T.textMuted,
      marginBottom: 3
    }} data-id="element-1479" data-mp="3bjakuiaiie9f">

        {label}
      </div>
      <div style={{
      fontSize: 14,
      color: T.text,
      fontWeight: 600
    }} data-id="element-1480" data-mp="1kez7oxaijt0m">

        {valor || '—'}
      </div>
    </div>;
}

// Pantalla pública: se muestra ANTES del login cuando la URL trae
// ?verificar=<token>. Es la copia digital a la que lleva el QR impreso en el
// formulario, para que quien recibe el papel pueda cotejarlo contra el
// sistema sin tener usuario.
export function VerificarPedido({
  token
}: {
  token: string;
}) {
  const [estado, setEstado] = useState<'cargando' | 'listo' | 'error'>('cargando');
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const res = await fetchPedidoPorToken(token);
      if (cancelado) return;
      if (res.error) {
        setError(res.error);
        setEstado('error');
        return;
      }
      // Un borrador no es un documento emitido: aunque alguien conociera el
      // token, no debe poder verlo hasta que esté autorizado.
      if (!res.pedido || res.pedido.estado === 'borrador') {
        setPedido(null);
        setEstado('listo');
        return;
      }
      setPedido(res.pedido);
      setEstado('listo');
    })();
    return () => {
      cancelado = true;
    };
  }, [token]);
  if (estado === 'cargando') {
    return <Marco data-id="element-1481" data-mp="3ey989m4zhygc">
        <div style={{
        ...glassLight,
        padding: 40,
        textAlign: 'center',
        color: T.textSecondary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12
      }} data-id="element-1482" data-mp="3f15ahmnv79al">

          <Loader2 size={26} style={{
          color: T.accent,
          animation: 'spin 1s linear infinite'
        }} data-id="element-1483" data-mp="318fld9d1ho99" />

          <style data-id="element-1484" data-mp="1kxq81yyta1b">{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
          Verificando documento…
        </div>
      </Marco>;
  }
  if (estado === 'error' || !pedido) {
    return <Marco data-id="element-1485" data-mp="3dfr3ufc313u4">
        <div style={{
        ...glassLight,
        padding: 40,
        textAlign: 'center'
      }} data-id="element-1486" data-mp="3dg0t20paz6yr">

          <FileWarning size={34} style={{
          color: T.danger,
          marginBottom: 14
        }} data-id="element-1487" data-mp="3ihgg1yb1jnjs" />

          <div style={{
          fontSize: 19,
          fontWeight: 800,
          color: T.text,
          marginBottom: 8
        }} data-id="element-1488" data-mp="34hx9h9xvok9l">

            Documento no encontrado
          </div>
          <div style={{
          fontSize: 14,
          color: T.textSecondary,
          lineHeight: 1.5
        }} data-id="element-1489" data-mp="2fleu25idek">

            {error ?? 'Este código no corresponde a ninguna orden de pedido emitida. ' + 'Si lo escaneaste de un documento impreso, verifícalo con la empresa ' + 'antes de despachar.'}
          </div>
        </div>
      </Marco>;
  }
  const anulado = pedido.estado === 'anulado';
  const total = pedido.lineas.reduce((s, l) => s + (l.galones || 0), 0);
  return <Marco data-id="element-1490" data-mp="nsibx5qf3bb2">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-1491" data-mp="ns9xh9enbxxr">

        {/* Veredicto arriba del todo: es lo único que importa de un vistazo */}
        <div style={{
        ...glassLight,
        padding: '22px 24px',
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        borderColor: anulado ? `${T.danger}44` : `${T.ok}44`,
        background: anulado ? T.dangerSoft : T.okSoft
      }} data-id="element-1492" data-mp="23i15scsu96yc">

          {anulado ? <Ban size={30} style={{
          color: T.danger,
          flexShrink: 0
        }} data-id="element-1493" data-mp="1pj9uey5d1zdw" /> : <BadgeCheck size={30} style={{
          color: T.ok,
          flexShrink: 0
        }} data-id="element-1494" data-mp="1klhrl936gsux" />}
          <div data-id="element-1495" data-mp="1zisav4m2b317">
            <div style={{
            fontSize: 18,
            fontWeight: 800,
            color: anulado ? T.danger : T.ok
          }} data-id="element-1496" data-mp="1zf4gk38smclf">

              {anulado ? 'Orden anulada' : 'Documento auténtico'}
            </div>
            <div style={{
            fontSize: 13,
            color: T.textSecondary,
            marginTop: 2
          }} data-id="element-1497" data-mp="35x9ohshiyut8">

              {anulado ? 'Esta orden fue anulada. No debe despacharse contra este documento.' : `Autorizada por ${pedido.autorizadoPor || '—'} el ${fmtFecha(pedido.autorizadoFecha)}.`}
            </div>
          </div>
        </div>

        <div style={{
        ...glassLight,
        padding: 24
      }} data-id="element-1498" data-mp="3mz6z5xy4bj8n">

          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 20
        }} data-id="element-1499" data-mp="3rwp7pn883wg4">

            <span style={{
            fontSize: 21,
            fontWeight: 800,
            color: T.text
          }} data-id="element-1500" data-mp="2z8tamheqs2it">

              Orden {pedido.numeroOrden}
            </span>
            <Badge label={etiquetaTipo(pedido.tipo)} color={esWOP(pedido.tipo) ? T.textMuted : T.brand} soft={esWOP(pedido.tipo) ? T.surfaceSubtle : T.accentSoft} dot={false} data-id="element-1501" data-mp="38yw2gea4nafk" />

          </div>

          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))',
          gap: 18,
          marginBottom: 24
        }} data-id="element-1502" data-mp="2bhnv63cprc91">

            <Dato label="Fecha" valor={fmtFecha(pedido.fecha)} data-id="element-1503" data-mp="xmnvi3yieh9h" />
            <Dato label="Suplidor" valor={suplidorDe(pedido.tipo).razonSocial} data-id="element-1504" data-mp="tmx1zshlln19" />
            <Dato label="Conductor" valor={pedido.conductor} data-id="element-1505" data-mp="om988z1zis3c" />
            <Dato label="Pase del chofer" valor={pedido.paseChofer} data-id="element-1506" data-mp="oolqldbnn4gx" />
            <Dato label="Ficha de la cola" valor={pedido.fichaCola} data-id="element-1507" data-mp="jn63lfpx2nvw" />
            <Dato label="Placa del camión" valor={pedido.placaCamion} data-id="element-1508" data-mp="eouu563jafe7" />
            <Dato label="Total" valor={`${fmtGal(total)} galones`} data-id="element-1509" data-mp="9xwd24aq1d9w" />
          </div>

          <div style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: T.textMuted,
          marginBottom: 10
        }} data-id="element-1510" data-mp="9w1qkljsxd3p">

            Detalle del despacho
          </div>
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginBottom: 24
        }} data-id="element-1511" data-mp="3s26e4vt45dvv">

            {pedido.lineas.map((l, i) => <div key={i} style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 14,
            alignItems: 'baseline',
            flexWrap: 'wrap',
            padding: '10px 14px',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.rMd
          }} data-id="element-1512" data-mp="1fsy5h03u336l" data-mpi={i}>

                <div data-id="element-1513" data-mp="1avxz0o32nyey">
                  <div style={{
                fontSize: 14,
                fontWeight: 700,
                color: T.text
              }} data-id="element-1514" data-mp="w5gu2ur8aqdb">

                    {l.destino}
                  </div>
                  <div style={{
                fontSize: 12,
                color: T.textMuted
              }} data-id="element-1515" data-mp="n4sevwq3mjyu">

                    {PRODUCTO_LABEL[l.producto] ?? l.producto}
                    {esWOP(pedido.tipo) ? ` · Compart. ${l.compartimiento}` : ''}
                    {l.rncDestino ? ` · RNC ${l.rncDestino}` : ''}
                  </div>
                </div>
                <div style={{
              fontSize: 15,
              fontWeight: 800,
              color: T.accent,
              whiteSpace: 'nowrap'
            }} data-id="element-1516" data-mp="i7a7wbvn6eth">

                  {fmtGal(l.galones)} gal
                </div>
              </div>)}
          </div>

          <button type="button" onClick={() => imprimirPedido(pedido)} style={{
          ...primaryButton,
          width: '100%',
          justifyContent: 'center'
        }} data-id="element-1517" data-mp="8jema3kl9uns">

            <Printer size={15} data-id="element-1518" data-mp="hhgmwi0eulaj" /> Ver / descargar el formulario
          </button>
        </div>

        <div style={{
        textAlign: 'center',
        fontSize: 11,
        color: T.textMuted,
        marginTop: 18,
        lineHeight: 1.6
      }} data-id="element-1519" data-mp="dfvd55j6an4k">

          Código de verificación{' '}
          <span style={{
          fontFamily: 'monospace'
        }} data-id="element-1520" data-mp="ma9o4vwo7bhv">
            {pedido.token.slice(0, 12)}
          </span>
          <br data-id="element-1521" data-mp="116qzvo8rus4y" />
          Documento emitido por {EMPRESA_NOMBRE}.
        </div>
      </motion.div>
    </Marco>;
}